import { useRecordContext, useTranslate } from "ra-core";
import { EditButton } from "@/components/admin/edit-button";
import { DeleteButton } from "@/components/admin";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { ShowButton } from "@/components/admin/show-button";

import { AddTask } from "../tasks/AddTask";
import { TasksIterator } from "../tasks/TasksIterator";
import { TagsListEdit } from "./TagsListEdit";
import { ContactStatusSelector } from "./ContactInputs";
import { ContactPersonalInfo } from "./ContactPersonalInfo";
import { ContactBackgroundInfo } from "./ContactBackgroundInfo";
import { CustomFieldsSection } from "../../gravun/custom-fields/CustomFieldsSection";
import { AsideSection } from "../misc/AsideSection";
import { EngagementBadge } from "../../gravun/contacts/EngagementBadge";
import { AiAssistCard } from "../../gravun/ai/AiAssistCard";
import type { Contact } from "../types";
import { ContactMergeButton } from "./ContactMergeButton";
import { ExportVCardButton } from "./ExportVCardButton";
import { StatusBadge } from "../../gravun/data-display/StatusBadge";
import { leadSourceLabel, lifecycleStageLabel } from "./crmEnrichmentModel";
import { PrivacyActions } from "../../gravun/privacy/PrivacyActions";

export const ContactAside = ({ link = "edit" }: { link?: "edit" | "show" }) => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();

  if (!record) return null;

  return (
    <div className="hidden w-full text-sm sm:block">
      <div className="mb-4 -ml-1 flex flex-wrap items-center gap-1">
        {link === "edit" ? (
          <EditButton label="resources.contacts.action.edit" />
        ) : (
          <ShowButton label="resources.contacts.action.show" />
        )}
        {/* Data-subject requests belong on the record they concern, not in a
            settings screen someone has to go hunting through. */}
        <PrivacyActions
          contactId={Number(record.id)}
          contactName={`${record.first_name ?? ""} ${record.last_name ?? ""}`.trim()}
        />
      </div>

      <AsideSection title={translate("resources.notes.fields.status")}>
        <ContactStatusSelector />
      </AsideSection>

      <AsideSection title="Lifecycle">
        <ContactLifecyclePanel />
      </AsideSection>

      <AsideSection title="Engagement">
        <EngagementBadge />
      </AsideSection>

      <AsideSection title="AI assist">
        <AiAssistCard />
      </AsideSection>

      <AsideSection
        title={translate("resources.contacts.field_categories.personal_info")}
      >
        <ContactPersonalInfo />
      </AsideSection>

      <AsideSection
        title={translate("resources.contacts.field_categories.background_info")}
      >
        <ContactBackgroundInfo />
      </AsideSection>

      <CustomFieldsSection entity="contacts" />

      <AsideSection
        title={translate("resources.tags.name", { smart_count: 2 })}
      >
        <TagsListEdit />
      </AsideSection>

      <AsideSection
        title={translate("resources.tasks.name", { smart_count: 2 })}
      >
        <ReferenceManyField
          target="contact_id"
          reference="tasks"
          sort={{ field: "due_date", order: "ASC" }}
          perPage={1000}
        >
          <TasksIterator />
        </ReferenceManyField>
        <AddTask />
      </AsideSection>

      {link !== "edit" && (
        <>
          <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
            <ExportVCardButton />
            <ContactMergeButton />
          </div>
          <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
            <DeleteButton
              className="h-6 cursor-pointer hover:bg-destructive/10! text-destructive! border-destructive! focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"
              size="sm"
            />
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Lifecycle, attribution, and consent at a glance. Consent problems
 * (opt-outs, do-not-contact) surface as prominent badges because they are
 * enforced server-side, reps should see why a send will be refused.
 */
const ContactLifecyclePanel = () => {
  const record = useRecordContext<Contact>();
  if (!record) return null;

  const badges: { label: string; tone: "danger" | "warning" }[] = [];
  if (record.do_not_contact)
    badges.push({ label: "Do not contact", tone: "danger" });
  if (record.sms_consent === "opted_out")
    badges.push({ label: "SMS opted out", tone: "warning" });
  if (record.email_consent === "unsubscribed")
    badges.push({ label: "Email unsubscribed", tone: "warning" });

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <StatusBadge label={lifecycleStageLabel(record.lifecycle_stage)} />
        {record.source ? (
          <span className="text-muted-foreground">
            via {leadSourceLabel(record.source)}
            {record.source_detail ? ` (${record.source_detail})` : ""}
          </span>
        ) : null}
      </div>
      {badges.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {badges.map((badge) => (
            <StatusBadge
              key={badge.label}
              label={badge.label}
              tone={badge.tone}
            />
          ))}
        </div>
      ) : null}
      {record.address || record.city ? (
        <p className="text-muted-foreground">
          {[record.address, record.city, record.state_abbr, record.zipcode]
            .filter(Boolean)
            .join(", ")}
        </p>
      ) : null}
    </div>
  );
};
